import AddIcon from '@mui/icons-material/Add';
import FlowActionButtons from 'components/Flow/FlowActionButtons';
import FlowMetadataEditModal from 'components/Flow/FlowMetadataEditModal';
import React, { useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import {
  Box, Chip, Divider, Paper, Stack, Tooltip,
} from '@mui/material';
import { HButton } from 'BaseComponents';
import { styled } from '@mui/material/styles';


const HeaderContainer = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(0.5),
  padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  marginBottom: theme.spacing(1),
}));

const VersionChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.success.main,
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  cursor: 'pointer',
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(0.5),
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.secondary.light,
  },
  '&:focus': {
    outline: 0,
    backgroundColor: theme.palette.secondary.light,
    borderColor: theme.palette.common.black,
  },
}));

const FlowBuilderField = ({
  flow, field, addButtonText, onClick, typographyProps,
}) => {
  if (flow[field]?.length > 0) {
    return (
      <StyledTypography
        onClick={(e) => onClick(e, field)}
        data-testid={`flow-${field}`}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClick(e, field);
          }
        }}
        aria-label={`Edit ${field}`}
        {...typographyProps}
      >
        {flow[field]}
      </StyledTypography>
    );
  }
  return (
    <HButton
      variant='text'
      startIcon={<AddIcon />}
      size='small'
      onClick={(e) => onClick(e, field)}
      aria-label={`Add ${field}`}
    >
      {addButtonText}
    </HButton>
  );
};

const FlowHeaderInfo = ({ flow, cleanRoomFlow, isViewingDifferentVersion }) => (
  <Stack direction='row' gap={1} alignItems='center'>
    <Tooltip title='Flow Display ID'>
      <Chip
        label={flow.displayID}
        size='small'
        aria-label={`Flow ID: ${flow.displayID}`}
      />
    </Tooltip>

    <Stack direction='row' gap={1} alignItems='center'>
      {isViewingDifferentVersion && (
        <Tooltip title={`Previewing version ${flow.version}`}>
          <VersionChip
            label={`v${flow.version} Preview`}
            size='small'
          />
        </Tooltip>
      )}

      <Tooltip title='Active version for editing the flow and new runs (manual, schedule and reusable)'>
        <VersionChip
          label={`v${cleanRoomFlow?.flowVersion}${isViewingDifferentVersion ? ' Active' : ''}`}
          size='small'
        />
      </Tooltip>
    </Stack>
  </Stack>
);

const FlowHeaderContent = ({
  flow, editMode, isViewMode, handleOpen,
}) => (
  <Box flex={1}>
    <Stack gap={0.5}>
      {editMode && (
        <>
          <FlowBuilderField
            flow={flow}
            field='name'
            addButtonText='Add Title'
            onClick={handleOpen}
            typographyProps={{
              variant: 'h5',
            }}
          />
          <FlowBuilderField
            flow={flow}
            field='description'
            addButtonText='Add Description'
            onClick={handleOpen}
            typographyProps={{
              variant: 'body2',
            }}
          />
        </>
      )}
      {isViewMode && (
        <>
          <Typography
            variant='h5'
            data-testid='flow-name'
          >
            {flow.name || 'Untitled Flow'}
          </Typography>
          {flow.description && (
            <Typography
              variant='body2'
              data-testid='flow-description'
            >
              {flow.description}
            </Typography>
          )}
        </>
      )}
    </Stack>
  </Box>
);

const FlowBuilderHeader = ({
  flow, cleanRoomFlow, isViewMode, editMode,
}) => {
  const [editingField, setEditingField] = useState();
  const [metadataEditModalOpen, setMetadataEditModalOpen] = useState(false);
  const isViewingDifferentVersion = useMemo(() => (
    flow?.version !== cleanRoomFlow?.flowVersion
  ), [flow, cleanRoomFlow]);

  const handleOpen = (event, val) => {
    event.stopPropagation();
    setEditingField(val);
    setMetadataEditModalOpen(true);
  };

  const handleClose = () => {
    setMetadataEditModalOpen(false);
  };

  return (
    <>
      <HeaderContainer>
        <Stack>
          <FlowHeaderInfo
            flow={flow}
            cleanRoomFlow={cleanRoomFlow}
            isViewingDifferentVersion={isViewingDifferentVersion}
          />

          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            gap={1}
            alignItems={{ xs: 'stretch', lg: 'center' }}
            justifyContent='space-between'
          >
            <FlowHeaderContent
              flow={flow}
              editMode={editMode}
              isViewMode={isViewMode}
              handleOpen={handleOpen}
            />

            <Divider
              orientation={{ xs: 'horizontal', lg: 'vertical' }}
              flexItem
              sx={{
                display: { xs: 'block', lg: 'block' },
                alignSelf: { lg: 'stretch' },
              }}
            />

            <FlowActionButtons />
          </Stack>
        </Stack>
      </HeaderContainer>

      {editMode && metadataEditModalOpen && (
        <FlowMetadataEditModal
          open={metadataEditModalOpen}
          field={editingField}
          onClose={handleClose}
        />
      )}
    </>
  );
};

export default FlowBuilderHeader;
